<template>
  <v-card-text class="pa-3">
    <div v-if="details">
      <div v-if="details.latest_location && details.latest_location.status > 0">
        <div class="font-weight-bold">
          <span>
            {{ $t("modules.home.lat") }}:
            {{ details.latest_location.coordinate[1] }}
          </span>
          ,
          <span class="ml-1">
            {{ $t("modules.home.long") }}:
            {{ details.latest_location.coordinate[0] }} </span
          >,
          <span class="ml-1">
            {{ $t("modules.home.altitude") }}:
            {{ details.latest_location.altitude }}m
          </span>
        </div>
        <small>
          {{ $t("modules.home.lastUpdated") }}:
          {{
            new Date(details.latest_location.time).toLocaleString($i18n.locale)
          }}
        </small>
      </div>
      <v-layout>
        <v-flex md6 class="pr-2">
          <h3 class="pr-2" v-if="details.halo_system_report">
            {{ $t("modules.home.firmware") }}:
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-chip v-on="on" small dark
                  >{{
                    details.halo_system_report.version.official ||
                      details.halo_system_report.version.core
                  }}
                  ({{
                    details.halo_system_report.version.official
                      ? $t("modules.home.official")
                      : $t("modules.home.core")
                  }})
                </v-chip>
              </template>
              <div>
                {{ details.halo_system_report.version.base }} ({{
                  $t("modules.home.base")
                }})
              </div>
              <div>
                {{ details.halo_system_report.version.core }} ({{
                  $t("modules.home.core")
                }})
              </div>
              <div>
                {{ details.halo_system_report.version.web }} ({{
                  $t("modules.home.web")
                }})
              </div>
            </v-tooltip>
          </h3>
          <h3 v-if="details.pairing_id">
            {{ $t("modules.home.sn") }}:
            <span id="device__serial-number">{{ details.pairing_id }}</span>
            <v-btn icon @click="copy(details.pairing_id)" id="copy_button">
              <v-icon size="20">mdi-content-copy</v-icon>
            </v-btn>
          </h3>
          <v-alert v-else color="red" text id="device__details-invalid-serial">
            {{ $t("modules.home.invalidSerial") }}
          </v-alert>
          <div v-if="details.halo_system_report">
            <div class="subtitle-1 font-weight-bold">
              {{ $t("modules.home.temperatureSensor") }}
            </div>
            <div class="display-1" id="device__temperature">
              <v-icon size="48">mdi-thermometer</v-icon>
              {{
                convertTemperature(
                  details.halo_system_report.temperature,
                  "C",
                  this.temp_unit
                )
              }}<sup>&deg;{{ this.temp_unit }}</sup>
            </div>
            <div>
              <v-switch
                dense
                color="primary"
                v-model="is_celsius"
                id="device__toggle-unit"
                label="°C"
              >
                <template v-slot:prepend>
                  <v-label>&deg;F</v-label>
                </template>
              </v-switch>
            </div>
          </div>
          <h3>
            {{ $t("modules.dataPath.applicationRules") }}
          </h3>
          <v-list
            dense
            v-if="permissions.indexOf(60) !== -1 || environmentManager"
          >
            <v-list-item
              v-for="item in details.rules"
              :key="item.id"
              :id="'device__application_rules-' + item.id"
            >
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }} {{ item.type ? "(" + item.type + ")" : "" }}
                  <v-icon :color="applicationStatusColor(item)">
                    mdi-record
                  </v-icon>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else>
            <v-alert
              color="red"
              text
              class="ma-2 mb-4"
              id="device__details-application-rules-error"
            >
              {{ $t("modules.home.notPermitted") }}
            </v-alert>
          </div>
        </v-flex>
        <v-flex md6>
          <h3>
            {{ $t("modules.home.mainStatus") }}:
            <v-icon :color="statusColor(details)">
              mdi-record
            </v-icon>
          </h3>
          <div v-if="details.halo_data_links_info">
            <v-layout wrap>
              <v-flex
                md3
                class="text-center mb-4 align-center"
                v-for="(item, index) in sortEth(
                  details.halo_data_links_info.nics_info.eth
                )"
                :key="'eth' + index"
              >
                <v-menu
                  right
                  :close-on-content-click="false"
                  :disabled="
                    !hasVlan(item, details.halo_data_links_info.nics_info) &&
                      getIPs(item).length === 0
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div>
                      <div class="caption">&nbsp;</div>
                      <v-badge
                        :color="
                          color(
                            getVlan(
                              item,
                              details.halo_data_links_info.nics_info
                            ),
                            details.isOnline,
                            'eth'
                          )
                        "
                        top
                        :value="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                      >
                        <template v-slot:badge>
                          <v-menu top :close-on-content-click="false">
                            <template v-slot:activator="activator">
                              <v-icon
                                v-on="activator.on"
                                v-bind="activator.attrs"
                              >
                                mdi-sitemap
                              </v-icon>
                            </template>
                            <v-card>
                              <v-card-text>
                                <div
                                  v-if="
                                    hasVlan(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  "
                                >
                                  {{ $t("modules.home.vlan") }}:
                                  {{
                                    getVlanId(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  }}
                                  <span
                                    v-if="
                                      getVlanDisplayName(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    "
                                    >({{
                                      vlan_name_by_nic_id[item.nic_id]
                                    }})</span
                                  >
                                </div>
                                <v-list
                                  dense
                                  v-if="
                                    getDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.wanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip.ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          textColor(
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'eth'
                                            )
                                          )
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip.ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip.ip)"
                                          :color="
                                            textColor(
                                              color(
                                                getVlan(
                                                  item,
                                                  details.halo_data_links_info
                                                    .nics_info
                                                ),
                                                details.isOnline,
                                                'eth'
                                              )
                                            )
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                                <v-list
                                  dense
                                  v-if="
                                    getNonDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.nonWanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getNonDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          color(
                                            getVlan(
                                              item,
                                              details.halo_data_links_info
                                                .nics_info
                                            ),
                                            details.isOnline,
                                            'eth'
                                          ) === 'grey'
                                            ? 'grey--text'
                                            : 'blue--text'
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip)"
                                          :color="
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'eth'
                                            ) === 'grey'
                                              ? 'grey'
                                              : 'blue'
                                          "
                                          dense
                                          >mdi-content-copy
                                        </v-icon>
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </template>
                        <v-icon
                          v-on="on"
                          v-bind="attrs"
                          :color="
                            hasVlan(
                              item,
                              details.halo_data_links_info.nics_info
                            )
                              ? 'grey'
                              : color(item, details.isOnline, 'eth')
                          "
                        >
                          {{
                            item.is_cable_plugged
                              ? "mdi-inbox"
                              : "mdi-inbox-remove"
                          }}
                        </v-icon>
                      </v-badge>
                      <div class="caption">
                        {{ parseName(item.nic_id) }}
                        <span v-if="item.alias">({{ item.alias }})</span>
                      </div>
                      <div class="caption" v-if="item.activity !== undefined">
                        {{ parseActivity(item.activity) }}
                      </div>
                    </div>
                  </template>
                  <v-card>
                    <v-card-text>
                      <div
                        v-if="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                        v-html="
                          $t('modules.home.addedToVlan', [
                            getVlanId(
                              item,
                              details.halo_data_links_info.nics_info
                            )
                          ])
                        "
                      ></div>
                      <v-list dense v-if="getDataLinksIPs(item).length > 0">
                        <v-subheader>{{
                          $t("modules.home.wanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item
                            v-for="ip in getDataLinksIPs(item)"
                            :key="ip.ip"
                          >
                            <v-list-item-content
                              :class="
                                textColor(color(item, details.isOnline, 'eth'))
                              "
                            >
                              <v-list-item-title>{{ ip.ip }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(ip.ip)"
                                :color="
                                  textColor(
                                    color(item, details.isOnline, 'eth')
                                  )
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                      <v-list dense v-if="getNonDataLinksIPs(item).length > 0">
                        <v-subheader>{{
                          $t("modules.home.nonWanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item
                            v-for="ip in getNonDataLinksIPs(item)"
                            :key="ip"
                          >
                            <v-list-item-content
                              :class="
                                item.is_cable_plugged
                                  ? color(item, details.isOnline, 'eth') ===
                                    'grey'
                                    ? 'grey--text'
                                    : 'blue--text'
                                  : 'grey--text'
                              "
                            >
                              <v-list-item-title>{{ ip }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(ip)"
                                :color="
                                  item.is_cable_plugged
                                    ? color(item, details.isOnline, 'eth') ===
                                      'grey'
                                      ? 'grey'
                                      : 'blue'
                                    : 'grey'
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>
              <v-flex
                md3
                class="text-center mb-4"
                v-for="(item, index) in details.halo_data_links_info.nics_info
                  .wifi"
                :key="'wifi' + index"
              >
                <v-menu
                  right
                  :close-on-content-click="false"
                  :disabled="
                    !hasVlan(item, details.halo_data_links_info.nics_info) &&
                      getIPs(item).length === 0
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <div class="caption">&nbsp;</div>
                      <v-badge
                        :color="
                          color(
                            getVlan(
                              item,
                              details.halo_data_links_info.nics_info
                            ),
                            details.isOnline,
                            'wifi'
                          )
                        "
                        top
                        icon="mdi-sitemap"
                        :value="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                      >
                        <img
                          :src="
                            require('@/assets/icons/' +
                              signalIcon(
                                signalBar(item.signal, 'wifi'),
                                color(item, details.isOnline, 'wifi'),
                                'wifi'
                              ))
                          "
                          width="18"
                          height="18"
                          v-on="on"
                          v-bind="attrs"
                        />
                        <template v-slot:badge>
                          <v-menu top :close-on-content-click="false">
                            <template v-slot:activator="activator">
                              <v-icon
                                v-on="activator.on"
                                v-bind="activator.attrs"
                              >
                                mdi-sitemap
                              </v-icon>
                            </template>
                            <v-card>
                              <v-card-text>
                                <div
                                  v-if="
                                    hasVlan(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  "
                                >
                                  {{ $t("modules.home.vlan") }}:
                                  {{
                                    getVlanId(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  }}
                                  <span
                                    v-if="
                                      getVlanDisplayName(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    "
                                    >({{
                                      vlan_name_by_nic_id[item.nic_id]
                                    }})</span
                                  >
                                </div>
                                <v-list
                                  dense
                                  v-if="
                                    getDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.wanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip.ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          textColor(
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'wifi'
                                            )
                                          )
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip.ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip.ip)"
                                          :color="
                                            textColor(
                                              color(
                                                getVlan(
                                                  item,
                                                  details.halo_data_links_info
                                                    .nics_info
                                                ),
                                                details.isOnline,
                                                'wifi'
                                              )
                                            )
                                          "
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                                <v-list
                                  dense
                                  v-if="
                                    getNonDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.nonWanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getNonDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          color(
                                            getVlan(
                                              item,
                                              details.halo_data_links_info
                                                .nics_info
                                            ),
                                            details.isOnline,
                                            'wifi'
                                          ) === 'grey'
                                            ? 'grey--text'
                                            : 'blue--text'
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip)"
                                          :color="
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'wifi'
                                            ) === 'grey'
                                              ? 'grey'
                                              : 'blue'
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </template>
                      </v-badge>
                      <div class="caption">
                        {{ $t("modules.home.wifi") }}
                        <span v-if="item.alias">({{ item.alias }})</span>
                      </div>
                      <div class="caption">{{ item.ssid }}</div>
                      <div
                        class="caption"
                        v-if="item.state !== undefined && item.state === 1"
                      >
                        {{ parseActivity(item.activity) }}
                      </div>
                    </div>
                  </template>
                  <v-card>
                    <v-card-text>
                      <div
                        v-if="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                        v-html="
                          $t('modules.home.addedToVlan', [
                            getVlanId(
                              item,
                              details.halo_data_links_info.nics_info
                            )
                          ])
                        "
                      ></div>
                      <v-list dense v-if="getDataLinksIPs(item).length > 0">
                        <v-subheader>{{
                          $t("modules.home.wanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item
                            v-for="ip in getDataLinksIPs(item)"
                            :key="ip.ip"
                          >
                            <v-list-item-content
                              :class="
                                textColor(color(item, details.isOnline, 'wifi'))
                              "
                            >
                              <v-list-item-title>{{ ip.ip }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(ip.ip)"
                                :color="
                                  textColor(
                                    color(item, details.isOnline, 'wifi')
                                  )
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                      <v-list dense v-if="getNonDataLinksIPs(item).length > 0">
                        <v-subheader>{{
                          $t("modules.home.nonWanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item
                            v-for="ip in getNonDataLinksIPs(item)"
                            :key="ip"
                          >
                            <v-list-item-content
                              :class="
                                color(item, details.isOnline, 'wifi') === 'grey'
                                  ? 'grey--text'
                                  : 'blue--text'
                              "
                            >
                              <v-list-item-title>{{ ip }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(ip)"
                                :color="
                                  color(item, details.isOnline, 'wifi') ===
                                  'grey'
                                    ? 'grey'
                                    : 'blue'
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>

              <v-flex
                md3
                class="text-center mb-6 align-center"
                v-for="(item, index) in details.halo_data_links_info.nics_info
                  .vpn"
                :key="'vpn' + index"
              >
                <v-menu
                  right
                  :close-on-content-click="false"
                  :disabled="
                    !hasVlan(item, details.halo_data_links_info.nics_info) &&
                      !item.link
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on">
                      <div class="caption">&nbsp;</div>
                      <v-badge
                        :color="
                          color(
                            getVlan(
                              item,
                              details.halo_data_links_info.nics_info
                            ),
                            details.isOnline,
                            'vpn'
                          )
                        "
                        top
                        icon="mdi-sitemap"
                        :value="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                      >
                        <v-icon
                          :color="color(item, details.isOnline, 'vpn')"
                          v-on="on"
                          v-bind="attrs"
                        >
                          mdi-key
                        </v-icon>
                        <template v-slot:badge>
                          <v-menu top :close-on-content-click="false">
                            <template v-slot:activator="activator">
                              <v-icon
                                v-on="activator.on"
                                v-bind="activator.attrs"
                              >
                                mdi-sitemap
                              </v-icon>
                            </template>
                            <v-card>
                              <v-card-text>
                                <div
                                  v-if="
                                    hasVlan(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  "
                                >
                                  {{ $t("modules.home.vlan") }}:
                                  {{
                                    getVlanId(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  }}
                                  <span
                                    v-if="
                                      getVlanDisplayName(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    "
                                    >({{
                                      vlan_name_by_nic_id[item.nic_id]
                                    }})</span
                                  >
                                </div>
                                <v-list
                                  dense
                                  v-if="
                                    getDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.wanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip.ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          color(
                                            getVlan(
                                              item,
                                              details.halo_data_links_info
                                                .nics_info
                                            ),
                                            details.isOnline,
                                            'vpn'
                                          ) === 'grey'
                                            ? 'grey--text'
                                            : ip.status
                                            ? 'green--text'
                                            : 'red--text'
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip.ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip)"
                                          :color="
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'vpn'
                                            ) === 'grey'
                                              ? 'grey'
                                              : ip.status
                                              ? 'green'
                                              : 'red'
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                                <v-list
                                  dense
                                  v-if="
                                    getNonDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.nonWanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getNonDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          color(
                                            getVlan(
                                              item,
                                              details.halo_data_links_info
                                                .nics_info
                                            ),
                                            details.isOnline,
                                            'vpn'
                                          ) === 'grey'
                                            ? 'grey--text'
                                            : 'blue--text'
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip)"
                                          :color="
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'vpn'
                                            ) === 'grey'
                                              ? 'grey'
                                              : 'blue'
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </template>
                      </v-badge>
                      <div class="caption">
                        {{ parseName(item.nic_id, "vlan") }}
                      </div>
                    </div>
                  </template>
                  <v-card>
                    <v-card-text>
                      <div
                        v-if="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                        v-html="
                          $t('modules.home.addedToVlan', [
                            getVlanId(
                              item,
                              details.halo_data_links_info.nics_info
                            )
                          ])
                        "
                      ></div>
                      <v-list dense v-if="item.link">
                        <v-subheader>{{
                          $t("modules.home.nonWanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item>
                            <v-list-item-content
                              :class="
                                color(item, details.isOnline, 'vpn') === 'grey'
                                  ? 'grey--text'
                                  : 'blue--text'
                              "
                            >
                              <v-list-item-title>{{
                                item.link
                              }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(item.link)"
                                :color="
                                  color(item, details.isOnline, 'vpn') ===
                                  'grey'
                                    ? 'grey'
                                    : 'blue'
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex
                md3
                class="text-center mb-2 align-center"
                v-for="(item, index) in details.halo_data_links_info.nics_info
                  .cellular"
                :key="'cellular' + index"
              >
                <v-menu
                  :close-on-content-click="false"
                  right
                  :disabled="
                    !hasVlan(item, details.halo_data_links_info.nics_info) &&
                      getIPs(item).length === 0
                  "
                >
                  <template v-slot:activator="{ on, attrs }">
                    <div>
                      <v-badge
                        :color="
                          color(
                            getVlan(
                              item,
                              details.halo_data_links_info.nics_info
                            ),
                            details.isOnline,
                            'cellular'
                          )
                        "
                        top
                        icon="mdi-sitemap"
                        :value="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                      >
                        <img
                          v-if="item.signal_to_noise !== undefined"
                          :src="
                            require('@/assets/icons/' +
                              signalIcon(
                                signalBarForSignalToNoise(item.signal_to_noise),
                                color(item, details.isOnline, 'cellular'),
                                'cellular'
                              ))
                          "
                          width="18"
                          height="18"
                          v-on="on"
                          v-bind="attrs"
                        />
                        <img
                          v-else
                          :src="
                            require('@/assets/icons/' +
                              signalIcon(
                                signalBar(item.rssi, 'cellular'),
                                color(item, details.isOnline, 'cellular'),
                                'cellular'
                              ))
                          "
                          width="18"
                          height="18"
                          v-on="on"
                          v-bind="attrs"
                        />
                        <template v-slot:badge>
                          <v-menu top :close-on-content-click="false">
                            <template v-slot:activator="activator">
                              <v-icon
                                v-on="activator.on"
                                v-bind="activator.attrs"
                              >
                                mdi-sitemap
                              </v-icon>
                            </template>
                            <v-card>
                              <v-card-text>
                                <div
                                  v-if="
                                    hasVlan(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  "
                                >
                                  {{ $t("modules.home.vlan") }}:
                                  {{
                                    getVlanId(
                                      item,
                                      details.halo_data_links_info.nics_info
                                    )
                                  }}
                                  <span
                                    v-if="
                                      getVlanDisplayName(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    "
                                    >({{
                                      vlan_name_by_nic_id[item.nic_id]
                                    }})</span
                                  >
                                </div>
                                <v-list
                                  dense
                                  v-if="
                                    getDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.wanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip.ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          textColor(
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'cellular'
                                            )
                                          )
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip.ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip.ip)"
                                          :color="
                                            textColor(
                                              color(
                                                getVlan(
                                                  item,
                                                  details.halo_data_links_info
                                                    .nics_info
                                                ),
                                                details.isOnline,
                                                'cellular'
                                              )
                                            )
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                                <v-list
                                  dense
                                  v-if="
                                    getNonDataLinksIPs(
                                      getVlan(
                                        item,
                                        details.halo_data_links_info.nics_info
                                      )
                                    ).length > 0
                                  "
                                >
                                  <v-subheader>{{
                                    $t("modules.home.nonWanLinkIPs")
                                  }}</v-subheader>
                                  <v-list-item-group>
                                    <v-list-item
                                      v-for="ip in getNonDataLinksIPs(
                                        getVlan(
                                          item,
                                          details.halo_data_links_info.nics_info
                                        )
                                      )"
                                      :key="ip"
                                    >
                                      <v-list-item-content
                                        :class="
                                          color(
                                            getVlan(
                                              item,
                                              details.halo_data_links_info
                                                .nics_info
                                            ),
                                            details.isOnline,
                                            'cellular'
                                          ) === 'grey'
                                            ? 'grey--text'
                                            : 'blue--text'
                                        "
                                      >
                                        <v-list-item-title>{{
                                          ip
                                        }}</v-list-item-title>
                                      </v-list-item-content>
                                      <v-list-item-icon>
                                        <v-icon
                                          @click="copy(ip)"
                                          :color="
                                            color(
                                              getVlan(
                                                item,
                                                details.halo_data_links_info
                                                  .nics_info
                                              ),
                                              details.isOnline,
                                              'cellular'
                                            ) === 'grey'
                                              ? 'grey'
                                              : 'blue'
                                          "
                                          dense
                                          >mdi-content-copy</v-icon
                                        >
                                      </v-list-item-icon>
                                    </v-list-item>
                                  </v-list-item-group>
                                </v-list>
                              </v-card-text>
                            </v-card>
                          </v-menu>
                        </template>
                      </v-badge>
                      <div class="caption">
                        {{ item.name || "N/A" }}
                        <span v-if="item.alias">({{ item.alias }})</span>
                      </div>
                      <div
                        class="caption text-truncate mb-3"
                        :title="networkType(item.network_type)"
                        v-html="networkType(item.network_type)"
                      ></div>
                      <div class="caption" v-if="item.activity !== undefined">
                        {{ parseActivity(item.activity) }}
                      </div>
                    </div>
                  </template>
                  <v-card>
                    <v-card-text>
                      <div
                        v-if="
                          hasVlan(item, details.halo_data_links_info.nics_info)
                        "
                        v-html="
                          $t('modules.home.addedToVlan', [
                            getVlanId(
                              item,
                              details.halo_data_links_info.nics_info
                            )
                          ])
                        "
                      ></div>
                      <v-list dense>
                        <div v-if="item.iccid">
                          <v-subheader>{{
                            $t("modules.home.iccid")
                          }}</v-subheader>
                          <v-list-item-group>
                            <v-list-item>
                              <v-list-item-content>
                                <v-list-item-title>{{
                                  item.iccid || "N/A"
                                }}</v-list-item-title>
                              </v-list-item-content>
                              <v-list-item-icon>
                                <v-icon @click="copy(item.iccid)" dense
                                  >mdi-content-copy</v-icon
                                >
                              </v-list-item-icon>
                            </v-list-item>
                          </v-list-item-group>
                        </div>
                        <div v-if="getDataLinksIPs(item).length > 0">
                          <v-subheader>{{
                            $t("modules.home.wanLinkIPs")
                          }}</v-subheader>
                          <v-list-item-group>
                            <v-list-item
                              v-for="ip in getDataLinksIPs(item)"
                              :key="ip.ip"
                            >
                              <v-list-item-content
                                :class="
                                  textColor(
                                    color(item, details.isOnline, 'cellular')
                                  )
                                "
                              >
                                <v-list-item-title>{{
                                  ip.ip
                                }}</v-list-item-title>
                              </v-list-item-content>
                              <v-list-item-icon>
                                <v-icon
                                  @click="copy(ip.ip)"
                                  :color="
                                    textColor(
                                      color(item, details.isOnline, 'cellular')
                                    )
                                  "
                                  dense
                                  >mdi-content-copy</v-icon
                                >
                              </v-list-item-icon>
                            </v-list-item>
                          </v-list-item-group>
                        </div>
                      </v-list>
                      <v-list dense v-if="getNonDataLinksIPs(item).length > 0">
                        <v-subheader>{{
                          $t("modules.home.nonWanLinkIPs")
                        }}</v-subheader>
                        <v-list-item-group>
                          <v-list-item
                            v-for="ip in getNonDataLinksIPs(item)"
                            :key="ip"
                          >
                            <v-list-item-content
                              :class="
                                color(item, details.isOnline, 'cellular') ===
                                'grey'
                                  ? 'grey--text'
                                  : 'blue--text'
                              "
                            >
                              <v-list-item-title>{{ ip }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-icon>
                              <v-icon
                                @click="copy(ip)"
                                :color="
                                  color(item, details.isOnline, 'cellular') ===
                                  'grey'
                                    ? 'grey'
                                    : 'blue'
                                "
                                dense
                                >mdi-content-copy</v-icon
                              >
                            </v-list-item-icon>
                          </v-list-item>
                        </v-list-item-group>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-layout wrap>
              <v-flex
                md3
                class="text-center mb-4 align-center"
                v-for="(item, index) in filteredSatcomInfo"
                :key="'satcom' + index"
              >
                <div class="caption">&nbsp;</div>
                <satcom-info :item="item"></satcom-info>
              </v-flex>
            </v-layout>
            <div class="mb-2">
              <h3>
                {{ $t("modules.home.deviceTotalUpload") }}:
                {{ totalUpload(details.halo_data_links_info.nics_info) }}
              </h3>
              <h3>
                {{ $t("modules.home.deviceTotalDownload") }}:
                {{ totalDownload(details.halo_data_links_info.nics_info) }}
              </h3>
            </div>
          </div>
        </v-flex>
      </v-layout>
      <small v-if="details.halo_data_links_info">
        {{ $t("modules.home.lastUpdated") }}:
        {{
          new Date(details.halo_data_links_info.time_info.time).toLocaleString(
            $i18n.locale
          )
        }}
      </small>
    </div>
    <div
      v-else
      class="text-center pa-5 display-1 grey--text text--lighten-1"
      id="device__details-empty"
    >
      {{ $t("modules.home.selectDevice") }}
    </div>
    <v-snackbar v-model="copy_notifier" :timeout="2000" id="copied__snackbar">
      {{ $t("modules.home.copied") }}
      <v-btn color="blue" text @click="copy_notifier = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
  </v-card-text>
</template>

<script>
import util from "@/modules/home/util";
import SatcomInfo from "./satcom-info.vue";
import { SatcomStateEnum } from "@/utils/enums";

export default {
  name: "DeviceDetails",
  components: {
    SatcomInfo
  },
  data() {
    return {
      copy_notifier: false,
      temp_unit: "C",
      is_celsius: true,
      vlan_name_by_nic_id: {}
    };
  },
  props: {
    details: {
      type: Object,
      default: null
    },
    permissions: Array,
    environmentManager: Boolean
  },

  mounted() {
    let tempUnit = localStorage.getItem("temp_unit");
    if (!tempUnit) {
      localStorage.setItem("temp_unit", "C");
      this.is_celsius = true;
    } else {
      this.temp_unit = tempUnit;
      this.is_celsius = tempUnit === "C";
    }
  },
  computed: {
    filteredSatcomInfo() {
      if (!this.details.halo_data_links_info.satcom_modems_info) return [];
      return this.details.halo_data_links_info.satcom_modems_info.filter(
        item => item.satcom_state > SatcomStateEnum.DISABLED
      );
    }
  },

  watch: {
    is_celsius(value) {
      if (value === true) {
        this.temp_unit = "C";
        localStorage.setItem("temp_unit", "C");
      } else {
        this.temp_unit = "F";
        localStorage.setItem("temp_unit", "F");
      }
    }
  },

  methods: {
    statusColor(details) {
      if (details.is_server === true) {
        return "home__main_app_status__none";
      } else {
        return details.main_app_status
          ? "home__main_app_status__online"
          : "home__main_app_status__offline";
      }
    },
    applicationStatusColor(item) {
      return item.is_enabled
        ? "home__main_app_status__online"
        : "home__main_app_status__offline";
    },

    color(nic, isOnline, type = "eth") {
      if (!nic || !isOnline) {
        return "grey";
      }
      if (type === "vpn") {
        return this.getVpnNicColor(nic);
      }
      if (type === "cellular") {
        return this.getCellularNicColor(nic);
      } else {
        return this.getOtherNicColor(nic, type);
      }
    },
    getOtherNicColor(nic, type) {
      if (nic.status) {
        return this.getNicColorViaNicType(nic, type);
      } else {
        return this.getNicColorViaDataLinkStatus(nic);
      }
    },
    getNicColorViaNicType(nic, type) {
      if (type === "eth") {
        return this.getEthernetNicColor(nic);
      } else if (type === "wifi") {
        return this.getWifiNicColor(nic);
      } else if (type === "vlan") {
        return this.getVlanNicColor(nic);
      }
    },
    getEthernetNicColor(nic) {
      if (nic.status === 1) {
        if (nic.used_for_data) {
          return "green";
        } else {
          return "blue";
        }
      } else if (nic.status === 2 || nic.status === 4) {
        return "red";
      } else if (nic.status === 3) {
        return "grey";
      }
      return "grey";
    },
    getCellularNicColor(nic) {
      if (nic.signal_to_noise !== undefined) {
        if (nic.signal_to_noise >= -10) {
          return "green";
        } else if (nic.signal_to_noise < -10 && nic.signal_to_noise >= -15) {
          return "light-green";
        } else if (nic.signal_to_noise < -15 && nic.signal_to_noise >= -18) {
          return "yellow";
        } else if (nic.signal_to_noise < -18) {
          return "orange";
        }
        return "grey";
      } else {
        return this.getNicColorViaDataLinkStatus(nic);
      }
    },
    getWifiNicColor(nic) {
      if (nic.status === 1) {
        if (nic.state === 1) {
          return "green";
        } else if (nic.state === 2) {
          return "blue";
        }
      } else if (nic.status === 2) {
        return "red";
      } else if (nic.status === 3) {
        return "grey";
      }
      return "grey";
    },
    getVlanNicColor(nic) {
      if (nic !== undefined) {
        if (nic.status === 1) {
          if (nic.used_for_data) {
            return "green";
          } else {
            return "blue";
          }
        } else if (nic.status === 2) {
          return "red";
        }
      }
      return "grey";
    },
    getVpnNicColor(nic) {
      if (nic.link !== undefined && nic.link) {
        return "blue";
      }
      return "grey";
    },
    getNicColorViaDataLinkStatus(nic) {
      if (
        nic.data_links_status === undefined ||
        nic.data_links_status.length === 0
      ) {
        if (nic.is_cable_plugged) {
          return "blue";
        }
        return "grey";
      } else {
        let isOnline =
          nic.data_links_status.findIndex(item => item.status === 1) > -1;
        if (isOnline) {
          return "green";
        } else {
          return "red";
        }
      }
    },

    signalBar(val, type) {
      if (type === "wifi") {
        if (val > -50) {
          return 4;
        } else if (val > -60) {
          return 3;
        } else if (val > -70) {
          return 2;
        } else if (val > -80) {
          return 1;
        } else {
          return 0;
        }
      } else if (type === "cellular") {
        if (val === 99) {
          return 0;
        } else if (val > -80) {
          return 4;
        } else if (val > -90) {
          return 3;
        } else if (val > -100) {
          return 2;
        } else if (val > -110) {
          return 1;
        } else {
          return 0;
        }
      } else if (type === "eth") {
        return val;
      }
    },
    signalBarForSignalToNoise(val) {
      if (val >= -10) {
        return 4;
      } else if (val < -10 && val >= -15) {
        return 3;
      } else if (val < -15 && val >= -18) {
        return 2;
      } else if (val < -18) {
        return 1;
      }
      return 0;
    },

    signalIcon(bar, color, type) {
      if (color === "grey" || bar === 0) {
        return `signal_${type}_0_bar.svg`;
      } else {
        return `signal_${type}_${bar}_bar-${color}.svg`;
      }
    },
    textColor(color) {
      return `${color}--text`;
    },

    hasVlan(nic, data) {
      let vlans = data.vlan;
      if (vlans) {
        let nics_ids = vlans.reduce((total, item) => {
          return total.concat(item.nics_ids);
        }, []);
        return nics_ids.indexOf(nic.nic_id) > -1;
      } else {
        return false;
      }
    },

    getVlanId(nic, data) {
      let vlans = data.vlan;
      if (vlans) {
        let vlanId = "";
        vlans.some(item => {
          if (item.nics_ids.indexOf(nic.nic_id) > -1) {
            vlanId = item.nic_id;
            return true;
          }
        });
        return vlanId;
      } else {
        return "";
      }
    },

    getVlan(nic, data) {
      let vlans = data.vlan;
      if (vlans) {
        return vlans.find(item => {
          return item.nics_ids.indexOf(nic.nic_id) > -1;
        });
      }
      return null;
    },
    getVlanDisplayName(nic, data) {
      let vlans = data.vlan;
      let hasDisplayName = false;
      if (vlans) {
        vlans.forEach(item => {
          if (item.nics_ids.indexOf(nic.nic_id) > -1 && item.display_name) {
            this.vlan_name_by_nic_id[nic.nic_id] = item.display_name;
            hasDisplayName = true;
          }
        });
      }
      return hasDisplayName;
    },

    getIPs(item) {
      let ips = [];
      if (item && item.data_links_status) {
        ips = item.data_links_status.reduce((temp, data) => {
          return temp.concat(data.ip);
        }, []);
      }
      if (item && item.non_data_links) {
        ips = ips.concat(item.non_data_links);
      }
      return ips;
    },

    getDataLinksIPs(item) {
      let ips = [];
      if (item && item.data_links_status) {
        ips = item.data_links_status.reduce((temp, data) => {
          return temp.concat(data);
        }, []);
      }
      return ips;
    },

    getNonDataLinksIPs(item) {
      let ips = [];
      if (item && item.non_data_links) {
        ips = item.non_data_links;
      }
      return ips;
    },

    copy(text) {
      // Creates a temporary input to enable selection
      let input = document.createElement("input");
      input.setAttribute("value", text);
      document.body.appendChild(input);
      input.select();
      let result = document.execCommand("copy");
      document.body.removeChild(input);

      this.copy_notifier = true;
      return result;
    },

    networkType(type) {
      switch (type) {
        case 0:
          return "GSM";
        case 1:
          return "C-GSM";
        case 2:
          return "3G";
        case 3:
          return "GSM & EGPRS";
        case 4:
        case 5:
        case 6:
          return "3G & HSPA";
        case 7:
          return "LTE";
        case 8:
          return "LTE (o/ 5G)";
        case 9:
          return "5G NSA";
        case 10:
          return "5G";
        default:
          return "&nbsp;";
      }
    },

    sortEth(data) {
      return data.slice().sort((a, b) => {
        if (a.nic_id !== undefined && b.nic_id !== undefined) {
          if (a.nic_id < b.nic_id) {
            return -1;
          }
          if (a.nic_id > b.nic_id) {
            return 1;
          }
          return 0;
        }
        return 0;
      });
    },

    parseName(nic, type = "eth") {
      if (type === "eth") {
        let eth = {
          eth0: this.$t("modules.home.eth1"),
          eth1: this.$t("modules.home.eth2")
        };
        return eth[nic] || nic;
      } else if (type === "vlan") {
        if (nic.indexOf("tap") > -1) {
          return this.$t("modules.home.vpnL2");
        } else if (nic.indexOf("tun") > -1) {
          return this.$t("modules.home.vpnL3");
        } else {
          return nic;
        }
      } else {
        return nic;
      }
    },

    convertTemperature(value, from, to) {
      let temperature;
      if (from === "F" && to === "C") {
        temperature = (5 / 9) * (value - 32);
      } else if (from === "C" && to === "F") {
        temperature = (value * 9) / 5 + 32;
      } else {
        temperature = value;
      }
      // rounds up to two decimal places
      return Math.round((temperature + Number.EPSILON) * 10) / 10;
    },

    totalUpload(data) {
      let totalUpload = 0;
      if (data) {
        totalUpload =
          data.wifi.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.upload.avg || 0);
            }
            return total;
          }, 0) +
          data.eth.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.upload.avg || 0);
            }
            return total;
          }, 0) +
          data.cellular.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.upload.avg || 0);
            }
            return total;
          }, 0);

        if ("vlan" in data) {
          totalUpload += data.vlan.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.upload.avg || 0);
            }
            return total;
          }, 0);
        }

        if ("vpn" in data) {
          totalUpload += data.vpn.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.upload.avg || 0);
            }
            return total;
          }, 0);
        }
      }
      return (totalUpload / 1024).toFixed(2) + " Mbps";
    },

    totalDownload(data) {
      let totalDownload = 0;
      if (data) {
        totalDownload =
          data.wifi.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.download.avg || 0);
            }
            return total;
          }, 0) +
          data.eth.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.download.avg || 0);
            }
            return total;
          }, 0) +
          data.cellular.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.download.avg || 0);
            }
            return total;
          }, 0);

        if ("vlan" in data) {
          totalDownload += data.vlan.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.download.avg || 0);
            }
            return total;
          }, 0);
        }

        if ("vpn" in data) {
          totalDownload += data.vpn.reduce((total, item) => {
            if (item.net_stats) {
              return total + (item.net_stats.download.avg || 0);
            }
            return total;
          }, 0);
        }
      }
      return (totalDownload / 1024).toFixed(3) + " Mbps";
    },
    parseActivity: util.parseActivity
  }
};
</script>
